import { OnDragEndResponder } from "@hello-pangea/dnd";
import { compact, sortBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import {
  AssessmentType,
  Maybe,
  TalentIndicatorAssessmentQuestionNode,
  TalentIndicatorType,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import Dropdown from "@components/dropdown/dropdown";
import FormFields from "@components/form/form-fields";
import Input from "@components/input/input";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
} from "@components/toggle-button-group/toggle-button-group";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { assertNonNull } from "@helpers/helpers";

import ViewOptionsSidebar from "../../sidebar/view-options-sidebar";
import { ChartLabel, NineBoxQuestion } from "../nine-box/types";
import PerformanceAssessmentChartEditLabelsModal from "../performance-assessment-chart-edit-labels-modal";
import AssessmentReportFilterContainer from "./assessment-report-filter-container";
import { updateFilters } from "./helpers";
import TableColumnHider from "./table-column-hider";
import {
  AssessmentReportFilterMatchType,
  AssessmentReportFilterType,
  AssessmentReportTableColumn,
  AssessmentReportViewOptions,
} from "./types";

const AssessmentReportViewOptionsSidebar = ({
  assessmentType,
  calibrationGroupsEnabled,
  isOngoingProgram,
  viewOptions,
  talentIndicatorQuestions,
  onUpdateViewOptions,
  onClose,
  onUpdateChartLabels,
  onResetViewToBaseline,
  onSaveView,
  onSaveAsNewView,
  onDuplicateView,
  onDeleteView,
  onCreateCalibrationGroup,
  tableColumns = [],
  questions = [],
  chartLabels = [],
  recentAnswersQuestionIds = [],
}: {
  assessmentType: AssessmentType;
  calibrationGroupsEnabled?: boolean;
  isOngoingProgram: boolean;
  viewOptions: AssessmentReportViewOptions;
  talentIndicatorQuestions: (Pick<
    TalentIndicatorAssessmentQuestionNode,
    "id" | "startValue" | "endValue" | "labels"
  > & {
    talentIndicator?: Maybe<{
      id: number;
      name: string;
      customName?: Maybe<string>;
      talentIndicatorType: TalentIndicatorType;
    }>;
  })[];
  chartLabels?: ChartLabel[];
  tableColumns?: AssessmentReportTableColumn[];
  questions?: NineBoxQuestion[];
  recentAnswersQuestionIds?: number[];
  onUpdateViewOptions: (opts: Partial<AssessmentReportViewOptions>) => void;
  onClose: () => void;
  onUpdateChartLabels?: (labels: ChartLabel[]) => void;
  onResetViewToBaseline: () => void;
  onSaveView: () => void;
  onSaveAsNewView: () => void;
  onDuplicateView: () => void;
  onDeleteView: () => void;
  onCreateCalibrationGroup?: () => void;
}) => {
  const [isEditLabelModalOpen, setIsEditLabelModalOpen] = useState(false);

  const additionalActions = useMemo(() => {
    return (
      <>
        {(viewOptions.id || viewOptions.isDirty) &&
          calibrationGroupsEnabled &&
          onCreateCalibrationGroup && (
            <Button
              size={ButtonSize.large}
              theme={buttonTheme.text}
              text="Create Calibration Group"
              onClick={() => {
                onCreateCalibrationGroup();
                onClose();
              }}
            />
          )}
        {viewOptions.isDirty && (
          <>
            <Button
              size={ButtonSize.large}
              theme={buttonTheme.text}
              text="Reset"
              onClick={onResetViewToBaseline}
            />
            <Button
              size={ButtonSize.large}
              theme={buttonTheme.primary}
              className="flex items-center gap-1"
              onClick={onSaveView}
              text="Save View"
            />
          </>
        )}
        {viewOptions.id && (
          <Dropdown
            options={compact([
              {
                label: "Duplicate this view",
                onClick: onDuplicateView,
              },
              viewOptions.isDirty && {
                label: "Save as new view",
                onClick: onSaveAsNewView,
              },
              {
                label: "Delete this view",
                onClick: onDeleteView,
              },
            ])}
          />
        )}
      </>
    );
  }, [
    onDeleteView,
    onDuplicateView,
    onResetViewToBaseline,
    onSaveAsNewView,
    onSaveView,
    onCreateCalibrationGroup,
    onClose,
    viewOptions,
    calibrationGroupsEnabled,
  ]);

  const sortedTableColumns = useMemo(() => {
    const defaultPositions = tableColumns.reduce(
      (acc, col, index) => {
        acc[col.id] = index;
        return acc;
      },
      {} as Record<string, number>,
    );
    return sortBy(tableColumns, (col) => {
      const colIndex = viewOptions.tableColumns.findIndex(
        ({ id }) => id === col.id,
      );
      return colIndex === -1 ? defaultPositions[col.id] : colIndex;
    });
  }, [tableColumns, viewOptions.tableColumns]);

  const questionOptions = useMemo(() => {
    return questions.map((q, i) => {
      return {
        label: q.title,
        value: i,
      };
    });
  }, [questions]);
  const xQuestion = useMemo(
    () =>
      questions.length > 0
        ? questions[viewOptions.nineBox.selectedXQuestionIndex]
        : null,
    [questions, viewOptions.nineBox.selectedXQuestionIndex],
  );
  const yQuestion = useMemo(
    () =>
      questions.length > 1 &&
      viewOptions.nineBox.selectedYQuestionIndex !== null
        ? questions[viewOptions.nineBox.selectedYQuestionIndex]
        : null,
    [questions, viewOptions.nineBox.selectedYQuestionIndex],
  );

  const handleDragEnd: OnDragEndResponder = useCallback(
    ({ destination, source, reason }) => {
      if (reason === "DROP" && destination) {
        const newTableColumns = sortedTableColumns.map((col) => {
          const existingCol = viewOptions.tableColumns.find(
            ({ id }) => id === col.id,
          );
          return {
            id: col.id,
            hidden: existingCol ? existingCol.hidden : false,
          };
        });
        const idElm = newTableColumns.splice(source.index, 1)[0];
        newTableColumns.splice(destination.index, 0, idElm);
        onUpdateViewOptions({ tableColumns: newTableColumns });
      }
    },
    [sortedTableColumns, onUpdateViewOptions, viewOptions.tableColumns],
  );

  const onToggleColumnVisibility = useCallback(
    (columnId: string | number, hidden: boolean) => {
      const newTableColumns = sortedTableColumns.map((col) => {
        const existingCol = viewOptions.tableColumns.find(
          ({ id }) => id === col.id,
        );
        return {
          id: col.id,
          hidden:
            col.id === columnId
              ? hidden
              : existingCol
                ? existingCol.hidden
                : false,
        };
      });
      onUpdateViewOptions({ tableColumns: newTableColumns });
    },
    [sortedTableColumns, onUpdateViewOptions, viewOptions.tableColumns],
  );

  const handleChangeXQuestion = useCallback(
    (opt: ComboboxGenericOption<number>) => {
      onUpdateViewOptions({
        nineBox: {
          ...viewOptions.nineBox,
          selectedXQuestionIndex: opt.value,
        },
        filters: {
          ...viewOptions.filters,
          list: updateFilters(
            viewOptions.filters.list,
            AssessmentReportFilterType.ANSWER,
            [],
          ),
        },
      });
    },
    [onUpdateViewOptions, viewOptions.filters, viewOptions.nineBox],
  );
  const handleChangeYQuestion = useCallback(
    (opt: ComboboxGenericOption<number>) => {
      onUpdateViewOptions({
        nineBox: {
          ...viewOptions.nineBox,
          selectedYQuestionIndex: opt.value,
        },
        filters: {
          ...viewOptions.filters,
          list: updateFilters(
            viewOptions.filters.list,
            AssessmentReportFilterType.ANSWER,
            [],
          ),
        },
      });
    },
    [onUpdateViewOptions, viewOptions.filters, viewOptions.nineBox],
  );
  const handleSwitchQuestions = useCallback(() => {
    if (viewOptions.nineBox.selectedYQuestionIndex === null) {
      return;
    }
    onUpdateViewOptions({
      nineBox: {
        ...viewOptions.nineBox,
        selectedXQuestionIndex: viewOptions.nineBox.selectedYQuestionIndex,
        selectedYQuestionIndex: viewOptions.nineBox.selectedXQuestionIndex,
      },
      filters: {
        ...viewOptions.filters,
        list: updateFilters(
          viewOptions.filters.list,
          AssessmentReportFilterType.ANSWER,
          [],
        ),
      },
    });
  }, [onUpdateViewOptions, viewOptions.nineBox, viewOptions.filters]);
  const handleClearYQuestion = useCallback(() => {
    onUpdateViewOptions({
      nineBox: {
        ...viewOptions.nineBox,
        selectedYQuestionIndex: null,
      },
      filters: {
        ...viewOptions.filters,
        list: updateFilters(
          viewOptions.filters.list,
          AssessmentReportFilterType.ANSWER,
          [],
        ),
      },
    });
  }, [onUpdateViewOptions, viewOptions.nineBox, viewOptions.filters]);

  return (
    <ViewOptionsSidebar onClose={onClose} additionalActions={additionalActions}>
      <div className="px-6 pb-6">
        <FormFields
          width={120}
          fields={[
            {
              title: "View Name",
              render: () => (
                <Input
                  placeholder="Enter a name..."
                  maxLength={512}
                  value={viewOptions.name}
                  onChange={(e) =>
                    onUpdateViewOptions({
                      name: e.target.value,
                    })
                  }
                />
              ),
            },
          ]}
        />
      </div>

      {assessmentType === AssessmentType.Performance && (
        <div className="border-t p-6">
          {isEditLabelModalOpen && xQuestion && onUpdateChartLabels && (
            <PerformanceAssessmentChartEditLabelsModal
              onClose={() => setIsEditLabelModalOpen(false)}
              xQuestion={xQuestion}
              yQuestion={yQuestion}
              chartLabels={chartLabels}
              onChartLabelsSaved={onUpdateChartLabels}
            />
          )}
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold">Matrix (9-box)</div>
            <Button
              theme={buttonTheme.lightBlue}
              className="bg-transparent -mr-[10px]"
              onClick={() => setIsEditLabelModalOpen(true)}
              text="Edit Cell Labels"
            />
          </div>
          <div>
            {questions.length > 1 && (
              <>
                <div className="flex items-center">
                  <div className="font-bold w-18 shrink-0">X:</div>
                  <div className="flex-1 w-0">
                    <ComboboxGeneric
                      options={questionOptions}
                      onChangeValue={handleChangeXQuestion}
                      value={
                        questionOptions[
                          viewOptions.nineBox.selectedXQuestionIndex
                        ]
                      }
                    />
                  </div>
                </div>
                <div className="my-2 flex justify-center">
                  <Button
                    icon={TopicflowIconMap.switch}
                    theme={buttonTheme.iconGray}
                    disabled={
                      viewOptions.nineBox.selectedYQuestionIndex === null
                    }
                    onClick={handleSwitchQuestions}
                  />
                </div>
                <div className="flex items-center">
                  <div className="font-bold w-18 shrink-0">Y:</div>
                  <div className="flex-1 w-0">
                    <ComboboxGeneric
                      clearable
                      options={questionOptions}
                      onClearValue={handleClearYQuestion}
                      onChangeValue={handleChangeYQuestion}
                      value={
                        viewOptions.nineBox.selectedYQuestionIndex !== null
                          ? questionOptions[
                              viewOptions.nineBox.selectedYQuestionIndex
                            ]
                          : null
                      }
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="border-t p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold">Filters</div>
          <ToggleButtonGroup
            theme={ToggleButtonGroupTheme.roundedCompact}
            buttons={[
              {
                title: "Match all filters",
                active:
                  viewOptions.filters.type ===
                  AssessmentReportFilterMatchType.MATCH_ALL,
                value: AssessmentReportFilterMatchType.MATCH_ALL,
              },
              {
                title: "Match any filters",
                active:
                  viewOptions.filters.type ===
                  AssessmentReportFilterMatchType.MATCH_ANY,
                value: AssessmentReportFilterMatchType.MATCH_ANY,
              },
            ]}
            onClick={(opt) =>
              onUpdateViewOptions({
                filters: {
                  ...viewOptions.filters,
                  type: assertNonNull(opt.value),
                },
              })
            }
          />
        </div>
        <div>
          <AssessmentReportFilterContainer
            assessmentType={assessmentType}
            isOngoingProgram={isOngoingProgram}
            filters={viewOptions.filters.list}
            talentIndicatorQuestions={talentIndicatorQuestions}
            onUpdateFilters={(newFilters) =>
              onUpdateViewOptions({
                filters: { ...viewOptions.filters, list: newFilters },
              })
            }
          />
        </div>
      </div>

      {assessmentType === AssessmentType.Performance &&
        sortedTableColumns.length > 0 && (
          <TableColumnHider
            viewOptionsTableColumns={viewOptions.tableColumns}
            sortedTableColumns={sortedTableColumns}
            recentAnswersQuestionIds={recentAnswersQuestionIds}
            hiddenRecentAnswers={viewOptions.hiddenRecentAnswers}
            onDragEnd={handleDragEnd}
            onToggleColumnVisibility={onToggleColumnVisibility}
            onUpdateViewOptions={onUpdateViewOptions}
          />
        )}
    </ViewOptionsSidebar>
  );
};

export default AssessmentReportViewOptionsSidebar;
